import React, { useState, useEffect } from 'react';
import { useDatabase } from '../contexts/DatabaseContext';
import { useTenant } from '../contexts/TenantContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiDownload, FiUpload, FiTrash2, FiHardDrive, FiCheckCircle, FiShield, FiAlertTriangle } = FiIcons;

export const Backup = () => {
  const { db, isReady } = useDatabase();
  const { activeTenant } = useTenant();
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(false);

  useEffect(() => {
    if (isReady) {
      loadBackups();
    }
  }, [isReady]);

  const loadBackups = async () => {
    try {
      const backupList = await db.backups.orderBy('createdAt').reverse().toArray();
      setBackups(backupList || []);
    } catch (error) {
      console.error('Error loading backups:', error);
    }
  };

  const createBackup = async () => {
    setLoading(true);
    try {
      const allForms = await db.forms.toArray();
      const allTemplates = await db.templates.toArray();
      const allSettings = await db.settings.toArray();

      const backupData = {
        version: '2.0.0',
        tenantId: activeTenant.id,
        tenantName: activeTenant.name,
        timestamp: new Date().toISOString(),
        forms: allForms,
        templates: allTemplates,
        settings: allSettings
      };

      const dataSize = JSON.stringify(backupData).length;

      await db.backups.add({
        createdAt: new Date(),
        size: dataSize,
        formCount: allForms.length,
        data: backupData
      });

      // Download backup file
      const blob = new Blob([JSON.stringify(backupData, null, 2)], {
        type: 'application/json'
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `certs-backup-${activeTenant.slug}-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);

      setExportSuccess(true);
      setTimeout(() => setExportSuccess(false), 3000);
      await loadBackups();
    } catch (error) {
      console.error('Error creating backup:', error);
      alert('Failed to create backup.');
    } finally {
      setLoading(false);
    }
  };

  const restoreBackup = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setLoading(true);
    try {
      const text = await file.text();
      const backupData = JSON.parse(text);

      if (window.confirm('Restore will import the backup certificates into your local database. Proceed?')) {
        if (backupData.forms && backupData.forms.length > 0) {
          await db.forms.bulkPut(backupData.forms);
        }
        if (backupData.templates && backupData.templates.length > 0) {
          await db.templates.bulkPut(backupData.templates);
        }
        if (backupData.settings && backupData.settings.length > 0) {
          await db.settings.bulkPut(backupData.settings);
        }

        alert('Backup data successfully imported into IndexedDB!');
        await loadBackups();
      }
    } catch (error) {
      console.error('Error restoring backup:', error);
      alert('Failed to restore backup. Invalid JSON schema.');
    } finally {
      setLoading(false);
      event.target.value = '';
    }
  };

  const deleteBackup = async (id) => {
    if (window.confirm('Are you sure you want to delete this backup entry?')) {
      try {
        await db.backups.delete(id);
        await loadBackups();
      } catch (error) {
        console.error('Error deleting backup:', error);
      }
    }
  };

  const downloadBackup = (backup) => {
    try {
      const blob = new Blob([JSON.stringify(backup.data, null, 2)], {
        type: 'application/json'
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `certs-snapshot-${new Date(backup.createdAt).toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading backup:', error);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-black tracking-tight text-slate-900 dark:text-slate-100">
            Data Storage & Backup Management
          </h1>
          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300">
            Local Dexie DB
          </span>
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
          Organization: <strong className="text-slate-700 dark:text-slate-300">{activeTenant.name}</strong> • Create encrypted JSON snapshots or restore previous audit records.
        </p>
      </div>

      {/* Backup Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5 shadow-sm space-y-4">
          <div className="flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-3">
            <SafeIcon icon={FiDownload} className="w-4 h-4 text-blue-500" />
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200">
              Export Tenant Snapshot
            </h2>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Export all locally stored certificate records, custom templates, and company branding into a portable JSON backup file.
          </p>
          <button
            onClick={createBackup}
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-xs font-semibold shadow-md transition-all disabled:opacity-50"
          >
            <SafeIcon icon={exportSuccess ? FiCheckCircle : FiDownload} className="w-4 h-4" />
            <span>{loading ? 'Creating Snapshot...' : exportSuccess ? 'Exported!' : 'Export JSON Backup'}</span>
          </button>
        </div>

        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5 shadow-sm space-y-4">
          <div className="flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-3">
            <SafeIcon icon={FiUpload} className="w-4 h-4 text-emerald-500" />
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200">
              Restore from Backup File
            </h2>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Import an existing backup JSON to recover certificates or synchronize records onto a new offline device.
          </p>
          <div className="relative">
            <input
              type="file"
              accept=".json"
              onChange={restoreBackup}
              disabled={loading}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
            <button
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-xl text-xs font-semibold transition-all border border-slate-200 dark:border-slate-700"
            >
              <SafeIcon icon={FiUpload} className="w-4 h-4" />
              <span>{loading ? 'Importing...' : 'Select JSON File to Restore'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Snapshot History */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5 shadow-sm space-y-4">
        <div className="flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-3">
          <SafeIcon icon={FiHardDrive} className="w-4 h-4 text-indigo-500" />
          <h2 className="text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200">
            Local Backup Archives ({backups.length})
          </h2>
        </div>

        {backups.length > 0 ? (
          <div className="space-y-3">
            {backups.map((backup) => (
              <div
                key={backup.id}
                className="flex items-center justify-between p-3.5 border border-slate-100 dark:border-slate-800 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 dark:bg-blue-950/40 text-blue-600 flex items-center justify-center">
                    <SafeIcon icon={FiShield} className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-slate-900 dark:text-slate-100">
                      Snapshot {new Date(backup.createdAt).toLocaleDateString()} at{' '}
                      {new Date(backup.createdAt).toLocaleTimeString()}
                    </h4>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400">
                      {backup.formCount} certificates • {formatFileSize(backup.size)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => downloadBackup(backup)}
                    className="px-2.5 py-1 text-xs font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 rounded-lg"
                  >
                    Download
                  </button>
                  <button
                    onClick={() => deleteBackup(backup.id)}
                    className="p-1.5 text-slate-400 hover:text-rose-600 rounded"
                    title="Delete Archive"
                  >
                    <SafeIcon icon={FiTrash2} className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-slate-400 text-xs">
            No local snapshot history available yet.
          </div>
        )}
      </div>
    </div>
  );
};

export default Backup;
